import { useCallback, useEffect, useState } from 'react';
import { Copy, KeyRound, X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';

function copyText(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).catch(() => {});
}

/** Modal tras crear empresa: muestra credenciales del administrador (una sola vez si es temporal). */
export function OrgProvisionSuccessModal({
  open,
  onClose,
  title,
  inviteCode,
  adminUser,
  adminCredentials,
  entityLabel = 'empresa',
}) {
  const { t } = useI18n();
  if (!open) return null;

  const email = adminCredentials?.email || adminUser?.email || '';
  const password = adminCredentials?.password || '';

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-4 bg-black/50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex justify-between items-start gap-3">
          <div>
            <h3 className="text-lg font-bold text-stone-900">{title}</h3>
            <p className="text-sm text-stone-600 mt-1">
              {t('org.provision.hint', 'Guarda estas credenciales. La contraseña temporal solo se muestra ahora.')}
            </p>
          </div>
          <button type="button" className="text-stone-500 hover:text-stone-800" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        {inviteCode ? (
          <div className="rounded-xl border border-lime-200 bg-lime-50/80 p-3 text-sm">
            <p className="font-medium text-stone-800">{t('users.invite_code', 'Código de invitación')}</p>
            <p className="font-mono text-base mt-1">{inviteCode}</p>
          </div>
        ) : null}

        {email ? (
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-stone-500">{t('org.provision.admin_email', 'Email administrador')}</p>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 rounded-lg bg-stone-100 px-3 py-2 text-stone-900">{email}</code>
                <button type="button" className="btn-secondary text-xs" onClick={() => copyText(email)}>
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            {password ? (
              <div>
                <p className="text-stone-500">{t('org.provision.admin_password', 'Contraseña')}</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-stone-900">{password}</code>
                  <button type="button" className="btn-secondary text-xs" onClick={() => copyText(password)}>
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-stone-600">
                {t('org.provision.password_set', 'Contraseña definida al crear la empresa.')}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
            {t('org.provision.no_admin', 'No se generó administrador automático. Créalo manualmente en Usuarios.')}
          </p>
        )}

        <button type="button" className="btn-primary w-full" onClick={onClose}>
          {t('common.understood', 'Entendido')}
        </button>
      </div>
    </div>
  );
}

/** Panel en edición: consulta operador y regenera contraseña. */
export default function OrgOperatorPanel({
  entityType,
  entityId,
  canManage = true,
}) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [newCreds, setNewCreds] = useState(null);
  const [customPassword, setCustomPassword] = useState('');
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionPassword, setProvisionPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState('');

  const basePath = entityType === 'trainer' ? '/trainers' : '/gyms';

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`${basePath}/${entityId}/operator`);
      setOperator(data?.operator || null);
      setInviteCode(data?.invite_code || '');
    } catch {
      setError(t('org.operator.load_error', 'No se pudo cargar el administrador'));
      setOperator(null);
    } finally {
      setLoading(false);
    }
  }, [basePath, entityId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const resetPassword = async () => {
    if (!entityId || !canManage) return;
    setResetting(true);
    setError('');
    try {
      const body = customPassword.trim().length >= 6 ? { password: customPassword.trim() } : {};
      const { data } = await api.post(`${basePath}/${entityId}/operator/reset-password`, body);
      if (data?.admin_credentials?.password) {
        setNewCreds(data.admin_credentials);
      } else {
        emitToast({ type: 'success', title: t('org.operator.password_updated', 'Contraseña actualizada') });
        setCustomPassword('');
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('org.operator.reset_error', 'No se pudo actualizar la contraseña'));
    } finally {
      setResetting(false);
    }
  };

  const provisionOperator = async () => {
    if (!entityId || !canManage) return;
    setProvisioning(true);
    setError('');
    try {
      const body = {};
      if (provisionEmail.trim()) body.admin_email = provisionEmail.trim().toLowerCase();
      if (provisionPassword.trim().length >= 6) body.admin_password = provisionPassword.trim();
      const { data } = await api.post(`${basePath}/${entityId}/operator/provision`, body);
      if (data?.admin_credentials?.password) {
        setNewCreds(data.admin_credentials);
      } else if (data?.admin_credentials?.email) {
        setNewCreds({ email: data.admin_credentials.email, password: '' });
        emitToast({ type: 'success', title: t('org.operator.created', 'Administrador creado') });
      }
      setProvisionEmail('');
      setProvisionPassword('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('org.operator.provision_error', 'No se pudo crear el administrador'));
    } finally {
      setProvisioning(false);
    }
  };

  if (!entityId) return null;

  return (
    <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
      <h5 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-lime-600" />
        {t('org.operator.title', 'Administrador de la empresa')}
      </h5>
      {loading ? (
        <p className="text-sm text-stone-500">{t('common.loading', 'Cargando…')}</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : operator ? (
        <div className="rounded-xl border border-slate-200 bg-stone-50 p-4 space-y-2 text-sm">
          <p><span className="text-stone-500">{t('common.name', 'Nombre')}:</span> <strong>{operator.nombre}</strong></p>
          <p><span className="text-stone-500">Email:</span> <code>{operator.email}</code></p>
          <p><span className="text-stone-500">{t('users.role', 'Rol')}:</span> {operator.rol}</p>
          {inviteCode ? (
            <p><span className="text-stone-500">{t('users.invite_code', 'Código')}:</span> <code>{inviteCode}</code></p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-stone-600">{t('org.operator.none', 'Sin administrador vinculado.')}</p>
          {canManage ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 space-y-3">
              <p className="text-sm text-amber-900">
                {t('org.operator.provision_hint', 'Este coach no tiene cuenta de login. Créala aquí para que pueda entrar al panel.')}
              </p>
              <label className="block text-sm">
                <span className="label text-xs">{t('org.provision.admin_email', 'Email administrador')}</span>
                <input
                  className="input mt-1"
                  type="email"
                  value={provisionEmail}
                  onChange={(e) => setProvisionEmail(e.target.value)}
                  placeholder={t('org.operator.email_placeholder', 'Vacío = email de contacto del coach')}
                />
              </label>
              <label className="block text-sm">
                <span className="label text-xs">{t('org.operator.custom_password', 'Contraseña (opcional, min. 6)')}</span>
                <input
                  className="input mt-1"
                  type="text"
                  value={provisionPassword}
                  onChange={(e) => setProvisionPassword(e.target.value)}
                  placeholder={t('org.operator.auto_password', 'Vacío = generar automática')}
                />
              </label>
              <button type="button" className="btn-primary" disabled={provisioning} onClick={provisionOperator}>
                {provisioning ? t('common.saving', 'Guardando…') : t('org.operator.create', 'Crear administrador')}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {newCreds?.password ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-900">{t('org.provision.new_password', 'Nueva contraseña (cópiala ahora)')}</p>
          <code className="block mt-2 font-mono">{newCreds.password}</code>
          <button type="button" className="btn-secondary text-xs mt-2" onClick={() => copyText(newCreds.password)}>
            {t('common.copy', 'Copiar')}
          </button>
        </div>
      ) : null}

      {canManage && operator ? (
        <div className="flex flex-wrap gap-2 items-end">
          <label className="flex-1 min-w-[200px]">
            <span className="label text-xs">{t('org.operator.custom_password', 'Nueva contraseña (opcional, min. 6)')}</span>
            <input
              className="input mt-1"
              type="text"
              value={customPassword}
              onChange={(e) => setCustomPassword(e.target.value)}
              placeholder={t('org.operator.auto_password', 'Vacío = generar automática')}
            />
          </label>
          <button type="button" className="btn-secondary" disabled={resetting} onClick={resetPassword}>
            {resetting ? t('common.saving', 'Guardando…') : t('org.operator.reset', 'Regenerar contraseña')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
